import { Moment } from 'moment';
import momentTimezone from 'moment-timezone';
// Types
import {
  PromoCodesChargeSubject,
  ProductTypesType,
} from 'api/graphql-global-types';
import { TimeZoneOption } from 'constants/timeZone';
import { TreeDataEntity } from './tree-data.entity';
import { TreeDataConverter } from '../converter/tree-data.converter';
import { PromocodeFormValuesInterface } from './promocode-form-values.interface';
// Constants
import { TIME_ZONE_OPTIONS } from 'constants/timeZone';

export class PromocodeInputData {
  name: string;
  startUtc: string;
  endUtc: string | null;
  tz: string;
  amount: number;
  usageLimit: number;
  perUserLimit: number;
  chargeSubject: PromoCodesChargeSubject;
  isSiteWide: boolean;
  merchProductIds: string[] | null;
  amaIds: string[] | null;
  streamIds: string[] | null;
  ticketEventIds: string[] | null;
  siteWideOptions?: ProductTypesType[] | null;

  public static fromObject({
    name,
    startDate,
    validityPeriod,
    amount,
    usageLimit,
    perUserLimit,
    chargeSubject,
    isSiteWide,
    storeProducts,
    timeZone,
    siteWideOptions,
  }: PromocodeFormValuesInterface): PromocodeInputData {
    const timeZoneOption = this.findTimeZoneOption(timeZone);

    if (!timeZoneOption) {
      throw new Error(`TimeZoneOption not found. Unknown ${timeZone}.`);
    }

    const data = new PromocodeInputData();

    data.name = name.trim();
    data.amount = amount;
    data.usageLimit = usageLimit ? parseInt(usageLimit, 10) : -1;
    data.perUserLimit = perUserLimit ? parseInt(perUserLimit, 10) : -1;
    data.chargeSubject = chargeSubject;
    data.isSiteWide = isSiteWide;
    data.siteWideOptions = siteWideOptions;
    data.tz = timeZoneOption.tzName;

    if (validityPeriod?.length) {
      const [periodStart, periodEnd] = validityPeriod;

      data.startUtc = this.toUtc(periodStart, data.tz);
      data.endUtc = this.toUtc(periodEnd, data.tz);
    } else {
      data.startUtc = this.toUtc(startDate, data.tz);
      data.endUtc = null;
    }

    if (storeProducts) {
      const { amaIds, merchProductIds, streamIds, ticketEventIds } =
        this.getProductIdsSorted(storeProducts);

      data.amaIds = amaIds.length ? amaIds : null;
      data.merchProductIds = merchProductIds.length ? merchProductIds : null;
      data.streamIds = streamIds.length ? streamIds : null;
      data.ticketEventIds = ticketEventIds.length ? ticketEventIds : null;
    } else {
      data.amaIds = null;
      data.merchProductIds = null;
      data.streamIds = null;
      data.ticketEventIds = null;
    }

    return data;
  }

  // The picker shows the browser's clock; the admin means that clock time in the selected time zone.
  private static toUtc(value: Moment, tzName: string): string {
    return momentTimezone
      .tz(value.format('YYYY-MM-DDTHH:mm:ss'), tzName)
      .toISOString();
  }

  private static getProductIdsSorted(storeProducts: TreeDataEntity[]) {
    const products: {
      merchProductIds: string[];
      amaIds: string[];
      streamIds: string[];
      ticketEventIds: string[];
    } = { merchProductIds: [], amaIds: [], streamIds: [], ticketEventIds: [] };

    storeProducts?.forEach((product) => {
      const { type, id } = TreeDataConverter.to(product);

      switch (type) {
        case 'MerchProduct': {
          products.merchProductIds.push(id);

          break;
        }

        case 'Ama': {
          products.amaIds.push(id);

          break;
        }

        case 'Stream': {
          products.streamIds.push(id);

          break;
        }

        case 'PromocodeTicketEventOption': {
          products.ticketEventIds.push(id);

          break;
        }
      }
    });

    return products;
  }

  private static findTimeZoneOption(
    tzCode: string
  ): TimeZoneOption | undefined {
    return TIME_ZONE_OPTIONS.find((option) => option.tzCode === tzCode);
  }
}
